import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  Plus, Trash2, Edit2, Eye, Download, Printer, Receipt, Wrench, ClipboardList, Loader2, Forward, FileUp, Mail, CheckCircle2, Inbox, Search, FileText, Paperclip, ShoppingCart, BadgeCheck,
} from "lucide-react";
import { api, apiClient, API } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { useAuth } from "@/lib/auth";
import { PageHeader, Empty, Spinner } from "@/components/UI";
import PDFModal from "@/components/PDFModal";
import { formatCurrency, formatDate } from "@/lib/format";
import DocEditor from "@/components/dms/DocEditor";
import { EmailModal } from "@/components/dms/EmailModal";
import { SendHistoryModal } from "@/components/dms/SendHistoryModal";
import { EditHistoryModal } from "@/components/dms/EditHistoryModal";
import { QuoteRequestsPanel } from "@/components/dms/QuoteRequestsPanel";
import { PaymentModal, paymentBadge } from "@/components/dms/PaymentModal";
import { StatementModal } from "@/components/dms/StatementModal";
import { empty, withKey, stripKey } from "@/components/dms/constants";
import { StatusMenu } from "@/components/dms/StatusMenu";

const TYPES = {
  quotations:  { docType: "quotation", title: "Quotations", short: "Quotation", icon: Receipt },
  invoices:    { docType: "invoice",   title: "Invoices",   short: "Invoice",   icon: FileText },
  "job-cards": { docType: "job_card",  title: "Job Cards",  short: "Job Card",  icon: Wrench },
  sdns:        { docType: "sdn",       title: "Service Delivery Notes", short: "SDN", icon: ClipboardList },
  lpos:        { docType: "lpo",       title: "Local Purchase Orders", short: "LPO", icon: ShoppingCart },
};


export default function DMS() {
  const { kind } = useParams();
  const meta = TYPES[kind] || TYPES.quotations;
  const docType = meta.docType;
  const priced = docType === "quotation" || docType === "invoice" || docType === "lpo";
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState({ open: false, doc: null });
  const [query, setQuery] = useState("");
  const [genId, setGenId] = useState(null);
  const [preview, setPreview] = useState({ open: false, url: null, doc: null });
  const [params, setParams] = useSearchParams();
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canCreate = docType !== "lpo" || isAdmin || user?.role === "manager";
  const nav = useNavigate();

  const approveLpoDoc = async (d) => {
    if (!window.confirm(`Approve ${d.serial}? Once approved it can be emailed to the supplier.`)) return;
    try { await api.approveLpo(d.id); toast(`${d.serial} approved`, "success"); load(); }
    catch (e) { toast(e?.response?.data?.detail || "Approval failed", "error"); }
  };

  const q = query.trim().toLowerCase();
  const shown = q
    ? docs.filter((d) =>
        [d.serial, d.client_name, d.title, d.client_email]
          .some((v) => (v || "").toLowerCase().includes(q)))
    : docs;

  const load = useCallback(async () => {
    setLoading(true);
    try { setDocs(await api.listDmsDocs({ doc_type: docType })); }
    catch { toast("Failed to load", "error"); }
    finally { setLoading(false); }
  }, [docType, toast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (params.get("new") === "1") {
      const supplierId = params.get("supplier");
      if (supplierId && docType === "lpo") {
        api.listSuppliers().then((sups) => {
          const sp = sups.find((x) => x.id === supplierId);
          setEditor({
            open: true,
            doc: sp ? {
              ...empty("lpo"),
              supplier_id: sp.id,
              client_name: sp.name,
              client_address: sp.address || "",
              client_contact_person: sp.contact_person || "",
              client_contact_phone: sp.phone || "",
              client_email: sp.email || "",
              client_branch: sp.tin || "",
              payment_terms: sp.payment_terms || empty("lpo").payment_terms,
            } : null,
          });
        }).catch(() => setEditor({ open: true, doc: null }));
        params.delete("supplier");
      } else {
        setEditor({ open: true, doc: null });
      }
      params.delete("new");
      setParams(params, { replace: true });
    }
    const fromBid = params.get("from_bid");
    if (fromBid) {
      // Fetch the bid and seed a new quotation
      api.listBids().then((bids) => {
        const b = bids.find((x) => x.id === fromBid);
        if (b) {
          const seeded = {
            ...empty(docType),
            client_name: b.client_name || "",
            title: b.title || "",
            project_location: b.client_name || "",
            currency: b.currency || "UGX",
            notes: b.scope_of_work || "",
            items: [
              {
                description: b.scope_of_work || b.title,
                unit_of_measure: "Lot",
                qty: 1,
                unit_price: b.bid_amount || 0,
                supplied_by: "Jero-One",
                serial_no: "",
              },
            ],
            // Link the bid in internal notes
            related_job_card: "",
          };
          setEditor({ open: true, doc: { ...seeded, _seeded_from_bid: fromBid } });
          toast(`Pre-filled quotation from bid: ${b.title}`, "success");
        }
      });
      params.delete("from_bid");
      setParams(params, { replace: true });
    }
    // (Job Card / Quotation deep-link cleanup intentionally omitted so that
    //  the URL remains the single source of truth and the link can be
    //  bookmarked or re-followed.)
    const fromJobCard = params.get("from_job_card");
    if (fromJobCard && docType === "sdn") {
      api.getDmsDoc(fromJobCard).then((jc) => {
        if (jc && jc.doc_type === "job_card") {
          const seeded = {
            ...empty(docType),
            client_name: jc.client_name || "",
            client_address: jc.client_address || "",
            client_branch: jc.client_branch || "",
            client_contact_person: jc.client_contact_person || "",
            client_contact_phone: jc.client_contact_phone || "",
            client_email: jc.client_email || "",
            title: jc.title || "",
            project_location: jc.project_location || "",
            currency: jc.currency || "UGX",
            related_job_card: jc.serial || "",
            notes: (jc.scope_tasks || []).join("\n") || jc.notes || "",
            items: (jc.items || []).map((it) => ({
              description: it.description || "",
              unit_of_measure: it.unit_of_measure || "Pcs",
              qty: it.qty || 1,
              unit_price: it.unit_price || 0,
              supplied_by: it.supplied_by || "Jero-One",
              serial_no: it.serial_no || "",
            })),
          };
          setEditor({ open: true, doc: seeded });
          toast(`Pre-filled SDN from Job Card ${jc.serial}`, "success");
        } else {
          toast("Source job card not found", "error");
        }
      }).catch(() => toast("Failed to load job card", "error"));
    }
    const fromQuotation = params.get("from_quotation");
    if (fromQuotation && docType === "invoice") {
      api.getDmsDoc(fromQuotation).then((qt) => {
        if (qt && qt.doc_type === "quotation") {
          const seeded = {
            ...empty("invoice"),
            client_name: qt.client_name || "",
            client_address: qt.client_address || "",
            client_branch: qt.client_branch || "",
            client_contact_person: qt.client_contact_person || "",
            client_contact_phone: qt.client_contact_phone || "",
            client_email: qt.client_email || "",
            title: qt.title || "",
            project_location: qt.project_location || "",
            currency: qt.currency || "UGX",
            tax_rate: qt.tax_rate ?? 18,
            payment_terms: qt.payment_terms || "",
            delivery_terms: qt.delivery_terms || "",
            prepared_by: qt.prepared_by || "",
            bank: qt.bank || "",
            related_quotation: qt.serial || "",
            notes: qt.notes || "",
            items: (qt.items || []).map(withKey),
          };
          setEditor({ open: true, doc: seeded });
          toast(`Invoice pre-filled from ${qt.serial}`, "success");
        } else {
          toast("Source quotation not found", "error");
        }
      }).catch(() => toast("Failed to load quotation", "error"));
    }
    if (fromQuotation && docType === "job_card") {
      api.getDmsDoc(fromQuotation).then((qt) => {
        if (qt && qt.doc_type === "quotation") {
          const seeded = {
            ...empty(docType),
            client_name: qt.client_name || "",
            client_address: qt.client_address || "",
            client_branch: qt.client_branch || "",
            client_contact_person: qt.client_contact_person || "",
            client_contact_phone: qt.client_contact_phone || "",
            client_email: qt.client_email || "",
            title: qt.title || "",
            project_location: qt.project_location || "",
            currency: qt.currency || "UGX",
            service_type: qt.service_type || "",
            notes: `Generated from accepted quotation ${qt.serial}.\n\n${qt.notes || ""}`.trim(),
            scope_tasks: (qt.items || [])
              .map((it) => it.description)
              .filter(Boolean),
            items: (qt.items || []).map((it) => ({
              description: it.description || "",
              unit_of_measure: it.unit_of_measure || "Pcs",
              qty: it.qty || 1,
              unit_price: it.unit_price || 0,
              supplied_by: it.supplied_by || "Jero-One",
              serial_no: it.serial_no || "",
            })),
          };
          setEditor({ open: true, doc: seeded });
          toast(`Pre-filled Job Card from Quotation ${qt.serial}`, "success");
        } else {
          toast("Source quotation not found", "error");
        }
      }).catch(() => toast("Failed to load quotation", "error"));
    }
  }, [docType, params, setParams, toast]);

  const save = async (form, opts = {}) => {
    try {
      // Strip client-side _key from items before hitting the API
      const payload = { ...form, items: stripKey(form.items) };
      let saved;
      if (editor.doc?.id) {
        saved = await api.updateDmsDoc(editor.doc.id, payload);
        toast("Updated", "success");
      } else {
        saved = await api.createDmsDoc(payload);
        toast(`Created ${saved.serial}`, "success");
      }
      setEditor({ open: false, doc: null });
      load();
      if (opts.thenAction) {
        // Generate PDF immediately and either print or download
        await generate(saved, opts.thenAction);
      }
    } catch (e) {
      toast(e?.response?.data?.detail || "Save failed", "error");
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Permanently delete ${d.serial}? This cannot be undone.`)) return;
    try { await api.deleteDmsDoc(d.id); toast("Permanently deleted", "success"); load(); }
    catch (e) { toast(e?.response?.data?.detail || "Delete failed", "error"); }
  };

  const proposalInput = useRef(null);
  const [importing, setImporting] = useState(false);
  const quoteImportInput = useRef(null);
  const [importingQuote, setImportingQuote] = useState(false);

  const importQuotationFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (files.length === 1) return importQuotationFile(files[0]);
    setImportingQuote(true);
    toast(`Reading ${files.length} quotations — a draft invoice will be created for each…`, "info");
    try {
      const { jobs } = await api.quotationImportBatch(files);
      const results = { done: [], failed: jobs.filter((j) => !j.job_id).map((j) => j.filename) };
      const open = new Map(jobs.filter((j) => j.job_id).map((j) => [j.job_id, j.filename]));
      const deadline = Date.now() + 6 * 60 * 1000;
      while (open.size && Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 4000));
        for (const [jid, fname] of Array.from(open.entries())) {
          try {
            const job = await api.specAnalyzeStatus(jid);
            if (job.status === "done") {
              results.done.push(job.result?.invoice_serial || fname);
              open.delete(jid);
            } else if (job.status === "error") {
              results.failed.push(`${fname} — ${job.error || "failed"}`);
              open.delete(jid);
            }
          } catch { /* transient — keep polling */ }
        }
      }
      open.forEach((fname) => results.failed.push(`${fname} (timed out)`));
      await load();
      if (results.done.length) toast(`${results.done.length} draft invoice(s) created: ${results.done.join(", ")}`, "success");
      if (results.failed.length) toast(`Could not import: ${results.failed.join("; ")}`, "error");
    } catch (e) {
      toast(e?.response?.data?.detail || "Batch import failed", "error");
    } finally {
      setImportingQuote(false);
      if (quoteImportInput.current) quoteImportInput.current.value = "";
    }
  };

  const importQuotationFile = async (file) => {
    if (!file) return;
    setImportingQuote(true);
    toast("Reading the quotation & extracting invoice details…", "info");
    try {
      const { job_id } = await api.quotationImport(file);
      let job = { status: "processing" };
      const deadline = Date.now() + 4 * 60 * 1000;
      while (job.status === "processing" && Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        job = await api.specAnalyzeStatus(job_id);
      }
      if (job.status !== "done") throw new Error(job.error || "Import timed out — try again.");
      const res = job.result;
      const seeded = {
        ...empty("invoice"),
        client_name: res.client_name || "",
        client_address: res.client_address || "",
        client_branch: res.client_branch || "",
        client_contact_person: res.client_contact_person || "",
        client_contact_phone: res.client_contact_phone || "",
        client_email: res.client_email || "",
        title: res.title || "",
        project_location: res.project_location || "",
        currency: res.currency || "UGX",
        tax_rate: res.tax_rate ?? 18,
        payment_terms: res.payment_terms || "",
        delivery_terms: res.delivery_terms || "",
        related_quotation: res.quotation_ref || "",
        items: (res.items || []).map(withKey),
      };
      setEditor({ open: true, doc: seeded });
      toast(`Invoice pre-filled from ${res.quotation_ref || "the quotation"} — review and save`, "success");
    } catch (e) {
      toast(e?.response?.data?.detail || e.message || "Quotation import failed", "error");
    } finally {
      setImportingQuote(false);
      if (quoteImportInput.current) quoteImportInput.current.value = "";
    }
  };
  const importProposal = async (file) => {
    if (!file) return;
    setImporting(true);
    toast("Reading proposal with AI — this takes a few seconds…", "info");
    try {
      const draft = await api.quotationFromProposal(file);
      let seeded;
      if (docType === "job_card") {
        seeded = {
          ...empty("job_card"),
          doc_type: "job_card",
          client_name: draft.client_name || "",
          client_address: draft.client_address || "",
          client_branch: draft.client_branch || "",
          client_contact_person: draft.client_contact_person || "",
          client_contact_phone: draft.client_contact_phone || "",
          client_email: draft.client_email || "",
          title: draft.title || "",
          project_location: draft.project_location || "",
          currency: draft.currency || "UGX",
          service_type: draft.title || "",
          notes: draft.notes || "",
          scope_tasks: (draft.items || []).map((it) => it.description).filter(Boolean),
          items: (draft.items && draft.items.length
            ? draft.items
            : empty("job_card").items
          ).map(withKey),
        };
      } else {
        seeded = {
          ...empty("quotation"),
          ...draft,
          items: (draft.items && draft.items.length
            ? draft.items
            : empty("quotation").items
          ).map(withKey),
        };
      }
      setEditor({ open: true, doc: seeded });
      toast(`${meta.short} pre-filled from proposal — review & save`, "success");
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not read that proposal", "error");
    } finally {
      setImporting(false);
      if (proposalInput.current) proposalInput.current.value = "";
    }
  };

  const markComplete = async (d) => {
    if (!window.confirm(`Mark ${d.serial} as complete? A draft delivery note will be created automatically.`)) return;
    try {
      await api.updateDmsDoc(d.id, { status: "completed" });
      toast(`${d.serial} completed — delivery note drafted`, "success");
      load();
    } catch (e) {
      toast(e?.response?.data?.detail || "Update failed", "error");
    }
  };

  const [emailModal, setEmailModal] = useState({ open: false, doc: null, to: "", message: "", sending: false });
  const openEmailModal = (d) =>
    setEmailModal({ open: true, doc: d, to: d.client_email || "", message: "", sending: false });
  const sendEmail = async () => {
    const { doc, to, message } = emailModal;
    if (!to || !to.includes("@")) { toast("Enter a valid recipient email", "error"); return; }
    setEmailModal((m) => ({ ...m, sending: true }));
    try {
      const res = await api.emailDmsDoc(doc.id, { to, message });
      toast(`Sent ${doc.serial} to ${res.to}`, "success");
      setEmailModal({ open: false, doc: null, to: "", message: "", sending: false });
      load();
    } catch (e) {
      toast(e?.response?.data?.detail || "Email failed", "error");
      setEmailModal((m) => ({ ...m, sending: false }));
    }
  };

  const generate = async (d, mode = "download") => {
    setGenId(d.id);
    try {
      await api.generateDmsPdf(d.id);
      const res = await apiClient.get(`/dms/documents/${d.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (mode === "preview") {
        setPreview({ open: true, url, doc: d });
        return; // don't auto-cleanup the blob URL — modal owns it
      } else if (mode === "print") {
        // Open in hidden iframe and call print
        const w = window.open(url, "_blank");
        if (w) {
          w.addEventListener("load", () => {
            try { w.focus(); w.print(); } catch { /* print blocked by browser */ }
          });
        }
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `${d.serial.replace(/\//g, "_")}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast("PDF ready", "success");
      }
      load();
    } catch (e) {
      toast(e?.response?.data?.detail || "PDF failed", "error");
    } finally {
      setGenId(null);
    }
  };

  const [scanningInbox, setScanningInbox] = useState(false);
  const scanInbox = async () => {
    setScanningInbox(true);
    toast("Scanning connected inboxes for quote requests…", "info");
    try {
      const r = await api.scanQuoteRequests();
      if (r.created?.length) {
        toast(`Drafted ${r.created.length} quotation(s): ${r.created.join(", ")}`, "success");
        load();
      } else {
        toast(`Scanned ${r.scanned} email(s) — no new quote requests found`, "info");
      }
    } catch (e) {
      toast(e?.response?.data?.detail || "Inbox scan failed — is Gmail connected?", "error");
    } finally {
      setScanningInbox(false);
    }
  };

  const [requests, setRequests] = useState({ open: false, entries: null });
  const toggleRequests = async () => {
    if (requests.open) { setRequests({ open: false, entries: null }); return; }
    setRequests({ open: true, entries: null });
    try {
      const entries = await api.listQuoteRequests();
      setRequests({ open: true, entries });
    } catch {
      setRequests({ open: true, entries: [] });
    }
  };

  const [historyModal, setHistoryModal] = useState({ open: false, doc: null, entries: null, resending: null });
  const [editHistModal, setEditHistModal] = useState({ open: false, doc: null });
  const [payModal, setPayModal] = useState({ open: false, doc: null });
  const [stmtOpen, setStmtOpen] = useState(false);
  const openHistory = async (d) => {
    setHistoryModal({ open: true, doc: d, entries: null, resending: null });
    try {
      const entries = await api.dmsEmailLog(d.id);
      setHistoryModal((m) => (m.open ? { ...m, entries } : m));
    } catch {
      setHistoryModal((m) => (m.open ? { ...m, entries: [] } : m));
    }
  };
  const resend = async (entry) => {
    setHistoryModal((m) => ({ ...m, resending: entry.id }));
    try {
      await api.emailDmsDoc(historyModal.doc.id, { to: entry.to, cc: entry.cc || [] });
      toast(`Resent to ${entry.to}`, "success");
      const entries = await api.dmsEmailLog(historyModal.doc.id);
      setHistoryModal((m) => (m.open ? { ...m, entries, resending: null } : m));
      load();
    } catch (e) {
      toast(e?.response?.data?.detail || "Resend failed", "error");
      setHistoryModal((m) => ({ ...m, resending: null }));
    }
  };

  return (
    <div data-testid={`page-dms-${kind}`}>
      <PageHeader
        icon={meta.icon}
        title={meta.title}
        subtitle="Auto-numbered serials · branded letterhead background · ready to print"
        action={
          <div className="flex items-center gap-2">
            {docType === "invoice" && (
              <>
                <input
                  ref={quoteImportInput}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.txt"
                  className="hidden"
                  data-testid="quote-import-file-input"
                  onChange={(e) => importQuotationFiles(e.target.files)}
                />
                <button
                  onClick={() => quoteImportInput.current?.click()}
                  className="btn btn-ghost"
                  disabled={importingQuote}
                  data-testid="import-quotation-btn"
                  title="Upload one or more previous quotations (PDF/DOC/image) — one opens for review, several become draft invoices automatically"
                >
                  {importingQuote ? <Loader2 size={14} className="animate-spin" /> : <FileUp size={14} />}
                  {importingQuote ? "Reading quotation…" : "Invoice from Quotation file"}
                </button>
              </>
            )}
            {docType === "quotation" && (
              <button
                onClick={() => setStmtOpen(true)}
                className="btn btn-ghost"
                data-testid="client-statement-btn"
                title="Download a per-client statement of quotations, payments and balances"
              >
                <Receipt size={14} /> Statements
              </button>
            )}
            {docType === "quotation" && (
              <button
                onClick={toggleRequests}
                className="btn btn-ghost"
                data-testid="quote-requests-btn"
                title="Detected email quote requests and their drafted quotations"
              >
                <Mail size={14} /> Email requests
              </button>
            )}
            {docType === "quotation" && (
              <button
                onClick={scanInbox}
                className="btn btn-ghost"
                disabled={scanningInbox}
                data-testid="scan-inbox-btn"
                title="Scan info@/support@ inboxes for customer quote requests and auto-draft quotations"
              >
                {scanningInbox ? <Loader2 size={14} className="animate-spin" /> : <Inbox size={14} />}
                {scanningInbox ? "Scanning…" : "Scan inbox"}
              </button>
            )}
            {(docType === "quotation" || docType === "job_card") && (
              <>
                <input
                  ref={proposalInput}
                  type="file"
                  accept=".pdf,.txt"
                  className="hidden"
                  data-testid="proposal-file-input"
                  onChange={(e) => importProposal(e.target.files?.[0])}
                />
                <button
                  onClick={() => proposalInput.current?.click()}
                  className="btn btn-ghost"
                  disabled={importing}
                  data-testid="from-proposal-btn"
                  title="Upload an accepted proposal and let AI pre-fill the quotation"
                >
                  {importing ? <Loader2 size={14} className="animate-spin" /> : <FileUp size={14} />}
                  {importing ? "Reading…" : "From Proposal"}
                </button>
              </>
            )}
            {canCreate && (
              <button onClick={() => setEditor({ open: true, doc: null })} className="btn btn-primary" data-testid="new-doc">
                <Plus size={14} /> New {meta.short}
              </button>
            )}
          </div>
        }
      />

      <QuoteRequestsPanel requests={requests} toggleRequests={toggleRequests} />

      {docs.length > 0 && (
        <div className="mb-3 relative max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            data-testid="dms-search-input"
            className="input !pl-9"
            placeholder={`Search ${meta.title.toLowerCase()} — serial, client or subject…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      )}

      <div className="card overflow-hidden">
        {loading ? (
          <div className="empty"><Spinner /> &nbsp;Loading…</div>
        ) : docs.length === 0 ? (
          <Empty
            icon={meta.icon}
            title={`No ${meta.title.toLowerCase()} yet`}
            description="Generate your first one — it'll be saved with a unique JOT serial."
            action={
              <button onClick={() => setEditor({ open: true, doc: null })} className="btn btn-primary">
                <Plus size={14} /> Create your first {meta.short.toLowerCase()}
              </button>
            }
          />
        ) : shown.length === 0 ? (
          <div className="empty" data-testid="dms-no-matches">
            No {meta.title.toLowerCase()} match “{query}”
          </div>
        ) : (
          <>
            {/* Mobile cards (< lg) */}
            <div className="lg:hidden divide-y divide-slate-100">
              {shown.map((d) => {
                const sub = (d.items || []).reduce((a, i) => a + (i.qty || 0) * (i.unit_price || 0), 0);
                const total = sub + (sub * (d.tax_rate || 0)) / 100;
                return (
                  <div
                    key={d.id}
                    data-testid={`row-${d.id}`}
                    className="p-4 space-y-2"
                    role="button"
                    tabIndex={0}
                    onClick={() => setEditor({ open: true, doc: d })}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setEditor({ open: true, doc: d }); }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-xs font-bold" style={{ color: "var(--green-700)" }}>{d.serial}</div>
                        <div className="text-sm font-semibold break-words mt-0.5">{d.title || "—"}</div>
                        <div className="text-xs muted mt-0.5 break-words">{d.client_name || "—"}</div>
                      </div>
                      <StatusMenu doc={d} onChanged={load} />
                    </div>
                    {docType === "lpo" && (
                      d.approval_status === "approved" ? (
                        <span className="text-[10px] font-bold text-emerald-700" data-testid={`lpo-approved-${d.id}-mobile`}>✓ Approved</span>
                      ) : isAdmin ? (
                        <button className="text-[10px] font-bold text-amber-700" data-testid={`lpo-approve-${d.id}-mobile`}
                          onClick={(e) => { e.stopPropagation(); approveLpoDoc(d); }}>
                          ⏳ Pending — Approve now
                        </button>
                      ) : (
                        <span className="text-[10px] font-bold text-amber-700">⏳ Pending admin approval</span>
                      )
                    )}
                    <div className="flex items-center justify-between text-xs">
                      <span className="muted">{formatDate(d.date)}</span>
                      {priced && (
                        <span className="font-semibold flex items-center gap-1.5">
                          {(docType === "invoice" || d.status === "accepted") && (
                            <button
                              data-testid={`payment-badge-${d.id}-mobile`}
                              onClick={(e) => { e.stopPropagation(); setPayModal({ open: true, doc: d }); }}
                              className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${paymentBadge(d).cls}`}
                            >
                              {paymentBadge(d).label}
                            </button>
                          )}
                          {formatCurrency(total, d.currency)}
                        </span>
                      )}
                    </div>
                    {d.last_emailed_to && (
                      <button
                        data-testid={`send-history-${d.id}-mobile`}
                        onClick={(e) => { e.stopPropagation(); openHistory(d); }}
                        className="text-[10px] muted hover:text-[#0e6939] truncate max-w-full text-left"
                      >
                        ✉ Sent to {d.last_emailed_to} · {formatDate(d.last_emailed_at)}
                      </button>
                    )}
                    {d.last_edited_by && (
                      <button
                        data-testid={`edit-history-${d.id}-mobile`}
                        onClick={(e) => { e.stopPropagation(); setEditHistModal({ open: true, doc: d }); }}
                        className="text-[10px] muted hover:text-[#0e6939] truncate max-w-full text-left"
                      >
                        ✎ Edited by {d.last_edited_by}{d.updated_at ? ` · ${formatDate(d.updated_at)}` : ""}
                      </button>
                    )}
                    <div className="flex items-center gap-3 pt-1 border-t border-slate-100 flex-wrap" onClick={(e) => e.stopPropagation()}>
                      <button
                        data-testid={`preview-${d.id}-mobile`}
                        onClick={() => generate(d, "preview")}
                        className="text-xs text-slate-600 flex items-center gap-1"
                      >
                        <Eye size={13} /> Preview
                      </button>
                      <button
                        data-testid={`dl-${d.id}-mobile`}
                        onClick={() => generate(d, "download")}
                        disabled={genId === d.id}
                        className="text-xs text-slate-600 flex items-center gap-1"
                      >
                        {genId === d.id ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                        PDF
                      </button>
                      <button
                        data-testid={`email-${d.id}-mobile`}
                        onClick={() => openEmailModal(d)}
                        className="text-xs text-slate-600 flex items-center gap-1"
                      >
                        <Mail size={13} /> Email
                      </button>
                      {docType === "quotation" && (
                        <button
                          data-testid={`to-job-card-${d.id}-mobile`}
                          onClick={() => nav(`/dms/job-cards?from_quotation=${d.id}`)}
                          className="text-xs text-slate-600 flex items-center gap-1"
                        >
                          <Forward size={13} /> Job Card
                        </button>
                      )}
                      {docType === "quotation" && (
                        <button
                          data-testid={`to-invoice-${d.id}-mobile`}
                          onClick={() => nav(`/dms/invoices?from_quotation=${d.id}`)}
                          className="text-xs text-slate-600 flex items-center gap-1"
                        >
                          <FileText size={13} /> Invoice
                        </button>
                      )}
                      {docType === "job_card" && d.status !== "completed" && (
                        <button
                          data-testid={`complete-${d.id}-mobile`}
                          onClick={() => markComplete(d)}
                          className="text-xs text-slate-600 flex items-center gap-1"
                        >
                          <CheckCircle2 size={13} /> Complete
                        </button>
                      )}
                      {docType === "job_card" && (
                        <button
                          data-testid={`to-sdn-${d.id}-mobile`}
                          onClick={() => nav(`/dms/sdns?from_job_card=${d.id}`)}
                          className="text-xs text-slate-600 flex items-center gap-1"
                        >
                          <Forward size={13} /> SDN
                        </button>
                      )}
                      {docType === "invoice" && d.lpo_file_id && (
                        <button
                          data-testid={`dl-lpo-${d.id}-mobile`}
                          onClick={() => window.open(`${API}/dms/documents/${d.id}/lpo`, "_blank")}
                          className="text-xs text-sky-600 flex items-center gap-1"
                        >
                          <Paperclip size={13} /> LPO
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          data-testid={`del-${d.id}-mobile`}
                          onClick={() => remove(d)}
                          className="text-xs text-red-600 flex items-center gap-1 ml-auto"
                          title="Permanently delete (admin only)"
                        >
                          <Trash2 size={13} /> Delete
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop table (>= lg) */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="tbl">
              <thead>
                <tr>
                  <th>Serial</th>
                  <th>Client</th>
                  <th>Subject</th>
                  <th>Date</th>
                  <th>Status</th>
                  {priced && <th style={{ textAlign: "right" }}>Total</th>}
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((d) => {
                  const sub = (d.items || []).reduce((a, i) => a + (i.qty || 0) * (i.unit_price || 0), 0);
                  const total = sub + (sub * (d.tax_rate || 0)) / 100;
                  return (
                    <tr key={d.id} data-testid={`row-desktop-${d.id}`}>
                      <td><span className="font-mono text-xs font-bold" style={{ color: "var(--green-700)" }}>{d.serial}</span></td>
                      <td>{d.client_name || "—"}</td>
                      <td className="muted">{d.title || "—"}</td>
                      <td className="muted">{formatDate(d.date)}</td>
                      <td>
                        <StatusMenu doc={d} onChanged={load} />
                        {docType === "lpo" && (
                          d.approval_status === "approved" ? (
                            <span className="block text-[10px] font-bold text-emerald-700 mt-1" data-testid={`lpo-approved-${d.id}`}
                              title={`Approved by ${d.approved_by}`}>
                              ✓ Approved{d.approved_by ? ` · ${d.approved_by.split(" ")[0]}` : ""}
                            </span>
                          ) : isAdmin ? (
                            <button className="block text-[10px] font-bold text-amber-700 hover:text-emerald-700 mt-1"
                              data-testid={`lpo-approve-${d.id}`} onClick={() => approveLpoDoc(d)}>
                              ⏳ Pending — Approve now
                            </button>
                          ) : (
                            <span className="block text-[10px] font-bold text-amber-700 mt-1" data-testid={`lpo-pending-${d.id}`}>
                              ⏳ Pending admin approval
                            </span>
                          )
                        )}
                        {d.last_emailed_to && (
                          <button
                            data-testid={`send-history-${d.id}`}
                            onClick={() => openHistory(d)}
                            className="block text-[10px] muted hover:text-[#0e6939] mt-1 truncate max-w-[160px]"
                            title="View send history"
                          >
                            ✉ {d.last_emailed_to} · {formatDate(d.last_emailed_at)}
                          </button>
                        )}
                        {d.last_edited_by && (
                          <button
                            data-testid={`edited-by-${d.id}`}
                            onClick={() => setEditHistModal({ open: true, doc: d })}
                            className="block text-[10px] muted hover:text-[#0e6939] mt-0.5 truncate max-w-[160px] text-left"
                            title={`View full edit history (last edited by ${d.last_edited_by})`}
                          >
                            ✎ {d.last_edited_by}{d.updated_at ? ` · ${formatDate(d.updated_at)}` : ""}
                          </button>
                        )}
                      </td>
                      {priced && (
                        <td style={{ textAlign: "right", fontWeight: 600 }}>
                          {formatCurrency(total, d.currency)}
                          {(docType === "invoice" || d.status === "accepted") && (
                            <button
                              data-testid={`payment-badge-${d.id}`}
                              onClick={() => setPayModal({ open: true, doc: d })}
                              className={`block ml-auto mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${paymentBadge(d).cls}`}
                              title="Update payment status"
                            >
                              {paymentBadge(d).label}
                              {d.payment_status === "partial" && d.amount_paid > 0 && (
                                <> · {formatCurrency(Math.max(total - d.amount_paid, 0), d.currency)} due</>
                              )}
                            </button>
                          )}
                        </td>
                      )}
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        {docType === "quotation" && (
                          <button
                            data-testid={`to-job-card-${d.id}`}
                            onClick={() => nav(`/dms/job-cards?from_quotation=${d.id}`)}
                            className="text-slate-500 hover:text-[#0e6939] mr-3"
                            title="Convert to Job Card"
                          >
                            <Forward size={14} />
                          </button>
                        )}
                        {docType === "quotation" && (
                          <button
                            data-testid={`to-invoice-${d.id}`}
                            onClick={() => nav(`/dms/invoices?from_quotation=${d.id}`)}
                            className="text-slate-500 hover:text-[#0e6939] mr-3"
                            title="Generate Invoice"
                          >
                            <FileText size={14} />
                          </button>
                        )}
                        {docType === "job_card" && d.status !== "completed" && (
                          <button
                            data-testid={`complete-${d.id}`}
                            onClick={() => markComplete(d)}
                            className="text-slate-500 hover:text-[#0e6939] mr-3"
                            title="Mark complete (auto-drafts a delivery note)"
                          >
                            <CheckCircle2 size={14} />
                          </button>
                        )}
                        {docType === "job_card" && (
                          <button
                            data-testid={`to-sdn-${d.id}`}
                            onClick={() => nav(`/dms/sdns?from_job_card=${d.id}`)}
                            className="text-slate-500 hover:text-[#0e6939] mr-3"
                            title="Convert to Service Delivery Note"
                          >
                            <Forward size={14} />
                          </button>
                        )}
                        {docType === "invoice" && d.lpo_file_id && (
                          <button
                            data-testid={`dl-lpo-${d.id}`}
                            onClick={() => window.open(`${API}/dms/documents/${d.id}/lpo`, "_blank")}
                            className="text-sky-600 hover:text-sky-800 mr-3"
                            title={`Download client LPO — ${d.lpo_file_name}${d.lpo_reference ? ` (ref ${d.lpo_reference})` : ""}`}
                          >
                            <Paperclip size={14} />
                          </button>
                        )}
                        <button
                          data-testid={`email-${d.id}`}
                          onClick={() => openEmailModal(d)}
                          className="text-slate-500 hover:text-[#0e6939] mr-3"
                          title="Email PDF to client"
                        >
                          <Mail size={14} />
                        </button>
                        <button
                          data-testid={`preview-${d.id}`}
                          onClick={() => generate(d, "preview")}
                          className="text-slate-500 hover:text-[#0e6939] mr-3"
                          title="Preview PDF"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          data-testid={`print-${d.id}`}
                          onClick={() => generate(d, "print")}
                          disabled={genId === d.id}
                          className="text-slate-500 hover:text-[#0e6939] mr-3"
                          title="Print PDF"
                        >
                          <Printer size={14} />
                        </button>
                        <button
                          data-testid={`dl-${d.id}`}
                          onClick={() => generate(d, "download")}
                          disabled={genId === d.id}
                          className="text-slate-500 hover:text-[#0e6939] mr-3"
                          title="Download PDF"
                        >
                          {genId === d.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                        </button>
                        <button
                          data-testid={`edit-${d.id}`}
                          onClick={() => setEditor({ open: true, doc: d })}
                          className="text-slate-500 hover:text-[#0e6939] mr-3"
                        >
                          <Edit2 size={14} />
                        </button>
                        {isAdmin && (
                          <button
                            data-testid={`del-${d.id}`}
                            onClick={() => remove(d)}
                            className="text-slate-500 hover:text-red-600"
                            title="Permanently delete (admin only)"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <DocEditor
        open={editor.open}
        onClose={() => setEditor({ open: false, doc: null })}
        onSave={(form, opts) => save(form, opts)}
        initial={editor.doc}
        docType={docType}
        meta={meta}
      />

      <PDFModal
        open={preview.open}
        url={preview.url}
        title={preview.doc ? `${preview.doc.serial} — ${preview.doc.title || preview.doc.client_name || "Document"}` : ""}
        onClose={() => {
          if (preview.url) URL.revokeObjectURL(preview.url);
          setPreview({ open: false, url: null, doc: null });
        }}
        onDownload={() => {
          if (!preview.url || !preview.doc) return;
          const a = document.createElement("a");
          a.href = preview.url;
          a.download = `${preview.doc.serial.replace(/\//g, "_")}.pdf`;
          a.click();
        }}
      />

      <EmailModal emailModal={emailModal} setEmailModal={setEmailModal} sendEmail={sendEmail} />
      <SendHistoryModal historyModal={historyModal} setHistoryModal={setHistoryModal} resend={resend} />
      <EditHistoryModal editHistModal={editHistModal} setEditHistModal={setEditHistModal} />
      <PaymentModal modal={payModal} setModal={setPayModal} onSaved={load} />
      <StatementModal open={stmtOpen} onClose={() => setStmtOpen(false)} />
    </div>
  );
}
